/**
 * Busy-guard for critical E2EE operations (encrypt/decrypt/ratchet-step).
 *
 * Counts concurrent ongoing operations. While busy:
 * - Auto-reload is deferred (App.tsx polls `isBusy()` before reloading).
 * - `beforeunload` prevents accidental browser refresh / tab close.
 *
 * Not a lock — doesn't serialize operations. Just a signal that a write-path
 * is in flight so we don't chop it off mid-way with a reload.
 */

let count = 0;
const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

export function beginBusy(): void {
  count++;
  notify();
}

export function endBusy(): void {
  count = Math.max(0, count - 1);
  notify();
}

export function isBusy(): boolean {
  return count > 0;
}

export async function withBusy<T>(fn: () => Promise<T>): Promise<T> {
  beginBusy();
  try {
    return await fn();
  } finally {
    endBusy();
  }
}

/** Subscribe to busy-state changes. Returns unsubscribe. */
export function onBusyChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
